import Papa from "papaparse"
import type { CreateSmithersDeviceRequest } from "@/types/smithers-device"
import type { SmithersBulkImportIssue, SmithersBulkImportValidationResult } from "@/types/smithers-bulk-import"

export const SMITHERS_BULK_IMPORT_HEADERS = ["clientId", "deviceId", "sotiDeviceId", "validationVendor", "facilityCode", "venueId", "areaCode", "deviceCode", "nteClubCode", "matchThreshold", "validationTimeoutMs", "requiresProVenueInstance", "proVenueInstanceId"] as const
const IDENTITY_HEADERS = ["clientId", "deviceId", "sotiDeviceId"] as const
const REQUIRED_HEADERS = [...IDENTITY_HEADERS, "requiresProVenueInstance"] as const
const SUPPORTED_HEADERS = new Set<string>(SMITHERS_BULK_IMPORT_HEADERS)

function parseRequiresProVenueInstance(raw: string | undefined): boolean {
  const value = raw?.trim() ?? ""
  if (!value) throw new Error("requiresProVenueInstance is required and must be TRUE or FALSE")
  if (value.toLowerCase() === "true") return true
  if (value.toLowerCase() === "false") return false
  throw new Error(`requiresProVenueInstance must be TRUE or FALSE, not "${value}"`)
}

export function toSmithersImportRequest(row: Record<string, string>): CreateSmithersDeviceRequest {
  const identity = { clientId: row.clientId?.trim() ?? "", deviceId: row.deviceId?.trim() ?? "", sotiDeviceId: row.sotiDeviceId?.trim() ?? "" }
  for (const key of IDENTITY_HEADERS) if (!identity[key]) throw new Error(`${key} is required`)
  for (const key of ["clientId", "deviceId"] as const) if (Array.from(identity[key]).length > 100) throw new Error(`${key} cannot be longer than 100 characters`)
  const request: CreateSmithersDeviceRequest = { ...identity, requiresProVenueInstance: parseRequiresProVenueInstance(row.requiresProVenueInstance) }
  for (const key of ["facilityCode", "venueId", "areaCode", "deviceCode", "nteClubCode"] as const) if (row[key]?.trim()) request[key] = row[key].trim()
  if (row.validationVendor?.trim()) {
    const vendor = row.validationVendor.trim().toLowerCase()
    if (vendor !== "orion" && vendor !== "provenue") throw new Error("validationVendor must be ProVenue or Orion")
    request.validationVendor = vendor === "orion" ? "Orion" : "ProVenue"
  }
  for (const key of ["matchThreshold", "validationTimeoutMs", "proVenueInstanceId"] as const) {
    const raw = row[key]?.trim()
    if (!raw || (key === "proVenueInstanceId" && !request.requiresProVenueInstance)) continue
    const value = Number(raw)
    const min = key === "proVenueInstanceId" ? 1 : 0
    const max = key === "matchThreshold" ? 2147483647 : Number.MAX_SAFE_INTEGER
    if (!/^[+-]?\d+$/.test(raw) || !Number.isSafeInteger(value) || value < min || value > max) throw new Error(`${key} must be an integer between ${min} and ${max}`)
    request[key] = value
  }
  return request
}

function withoutIgnoredInstanceId(row: Record<string, string>): Record<string, string> {
  return row.requiresProVenueInstance?.toLowerCase() === "false" && row.proVenueInstanceId ? { ...row, proVenueInstanceId: "" } : row
}

export function validateSmithersBulkImportFile(file: File, options: { requiredClientId?: string; validClientIds?: ReadonlySet<string> } = {}): Promise<SmithersBulkImportValidationResult> {
  const invalid = (message: string) => Promise.resolve({ headers: [], rows: [], issues: [{ message }], isValid: false })
  if (!file.name.toLowerCase().endsWith(".csv")) return invalid("Choose a file with a .csv extension.")
  if (file.size > 10 * 1024 * 1024) return invalid("CSV files must be 10 MB or smaller.")
  if (!file.size) return invalid("CSV file is empty.")
  return new Promise((resolve) => {
    Papa.parse<string[]>(file, {
      skipEmptyLines: true,
      complete: ({ data, errors }) => {
        const [rawHeaders = [], ...rawRows] = data
        const headers = rawHeaders.map((header) => header.trim())
        const rows = rawRows.map((values) => withoutIgnoredInstanceId(Object.fromEntries(headers.map((header, i) => [header, values[i]?.trim() ?? ""]))))
        const issues: SmithersBulkImportIssue[] = errors.map((error) => ({ message: `CSV parse error: ${error.message}` }))
        const seen = new Set<string>()
        for (const header of headers) {
          if (!header) issues.push({ message: "CSV contains an empty column header." })
          else if (!SUPPORTED_HEADERS.has(header)) issues.push({ field: header, message: `Unsupported column "${header}".` })
          if (seen.has(header)) issues.push({ field: header, message: `Duplicate column "${header}".` })
          seen.add(header)
        }
        for (const header of REQUIRED_HEADERS) if (!seen.has(header)) issues.push({ field: header, message: `Missing required column "${header}".` })
        if (!rows.length) issues.push({ message: "CSV file contains no data rows." })
        if (rows.length > 10_000) {
          issues.push({ message: "CSV files may contain at most 10,000 data rows." })
          resolve({ headers, rows, issues, isValid: false })
          return
        }
        const firstRowByDevice = new Map<string, number>()
        rows.forEach((row, i) => {
          const rowNumber = i + 2
          if (rawRows[i].length !== headers.length) issues.push({ rowNumber, field: "clientId", message: `Row ${rowNumber}: expected ${headers.length} values but found ${rawRows[i].length}.` })
          if (!row.clientId || (options.validClientIds && !options.validClientIds.has(row.clientId))) {
            issues.push({ rowNumber, field: "clientId", message: `Row ${rowNumber}: ${row.clientId ? `unknown clientId "${row.clientId}"` : "clientId is required"}.` })
          } else if (options.requiredClientId && row.clientId !== options.requiredClientId) {
            issues.push({ rowNumber, field: "clientId", message: `Row ${rowNumber}: clientId must be "${options.requiredClientId}" for this import.` })
          }
          try { toSmithersImportRequest(row) } catch (error) { issues.push({ rowNumber, message: `Row ${rowNumber}: ${error instanceof Error ? error.message : "Invalid device values"}.` }) }
          const identity = JSON.stringify([row.clientId, row.deviceId])
          if (row.clientId && row.deviceId) {
            const first = firstRowByDevice.get(identity)
            if (first) issues.push({ rowNumber, field: "deviceId", message: `Row ${rowNumber}: ${row.clientId} / ${row.deviceId} duplicates row ${first}.` })
            else firstRowByDevice.set(identity, rowNumber)
          }
        })
        resolve({ headers, rows, issues, isValid: issues.length === 0 })
      },
      error: (error) => { resolve({ headers: [], rows: [], issues: [{ message: `Unable to read CSV: ${error.message}` }], isValid: false }) },
    })
  })
}
